import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Info, UserPlus } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useUsersList } from "./use-users-list";
import { UsersFilters } from "./UsersFilters";
import { UsersTable } from "./UsersTable";
import { UsersTableSkeleton } from "./UsersTableSkeleton";
import { UsersListEmpty } from "./UsersListEmpty";
import { Pagination } from "@/shared/components/Pagination";
import { CreateUserDialog } from "./CreateUserDialog";
import { DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from "./constants";
import type { UserStatusFilter } from "./types";

export function UsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const canManage = currentUser?.role === Role.WANDERFIT_ADMIN;

  // Local UI state — query/role/status/page. Limit is fixed for now;
  // page-size selector is a follow-up.
  const [query, setQuery] = useState("");
  const [role, setRole] = useState<Role | null>(null);
  const [status, setStatus] = useState<UserStatusFilter>("all");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  // Reset to page 1 every time a filter that affects the result set
  // changes — otherwise paging-into-page-3 then narrowing the filter
  // can leave the table on an empty page.
  useEffect(() => {
    setPage(1);
  }, [query, role, status]);

  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  const filters = {
    query: debouncedQuery,
    role,
    status,
    page,
    limit: DEFAULT_PAGE_SIZE,
  };

  const { data, isLoading, isFetching, isError } = useUsersList(filters);
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasFilters = debouncedQuery.length > 0 || role !== null || status !== "all";

  const handleReset = () => {
    setQuery("");
    setRole(null);
    setStatus("all");
  };

  return (
    <div data-testid="users-screen" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="brand-eyebrow">{t("users.eyebrow")}</p>
          <h1 className="text-4xl">{t("users.title")}</h1>
          <p className="font-sans text-base font-normal text-muted-foreground">
            {t("users.subtitle")}
          </p>
        </div>
        {canManage && (
          <Button data-testid="open-create-user" onClick={() => setCreateOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t("users.actions.create")}
          </Button>
        )}
      </header>

      {!canManage && (
        <div
          data-testid="users-readonly-banner"
          className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
        >
          <Info className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <p className="font-sans text-sm font-normal text-muted-foreground">
            {t("users.readOnlyBanner")}
          </p>
        </div>
      )}

      <UsersFilters
        query={query}
        role={role}
        status={status}
        onQueryChange={setQuery}
        onRoleChange={setRole}
        onStatusChange={setStatus}
        onReset={handleReset}
      />

      {isLoading && <UsersTableSkeleton />}

      {isError && (
        <div
          data-testid="users-list-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">{t("users.list.error")}</p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && <UsersListEmpty hasFilters={hasFilters} />}

      {!isError && items.length > 0 && (
        <div
          data-testid="users-list-container"
          aria-busy={isFetching}
          className={isFetching ? "opacity-70 transition-opacity" : ""}
        >
          <UsersTable items={items} />
          <div className="mt-4">
            <Pagination
              page={page}
              limit={DEFAULT_PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={setPage}
              testIdPrefix="users"
            />
          </div>
        </div>
      )}

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(user) => {
          // Jump to the new user's detail page so the admin sees the
          // created record immediately + can trigger follow-up actions
          // (password reset retry, role tweak) without hunting for it
          // in the list.
          navigate(`/users/${user.id}`);
        }}
      />
    </div>
  );
}
